import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAdUnitData,
  getDomains,
} from '@/services/api';

import {
  CompactDateRangePicker,
  CompactDomainSelect,
  AdUnitView,
} from './index';
import NetworkSelector from '@/components/layout/NetworkSelector';
import { useAoTrocarConta } from '@/contexts/NetworkContext';

const CACHE_KEYS = {
  DATA_TYPE: 'dashboard_data_type',
  START_DATE: 'dashboard_start_date',
  END_DATE: 'dashboard_end_date',
  PROJECT_ID: 'dashboard_project_id',
  DOMAINS: 'dashboard_domains',
};

const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

export default function Dashboard() {
  const [startDate, setStartDate] = useState(() => {
    const cached = localStorage.getItem(CACHE_KEYS.START_DATE);
    return cached || getYesterday();
  });

  const [endDate, setEndDate] = useState(() => {
    const cached = localStorage.getItem(CACHE_KEYS.END_DATE);
    return cached || getYesterday();
  });

  const [selectedDomains, setSelectedDomains] = useState<string[]>(() => {
    const cached = localStorage.getItem(CACHE_KEYS.DOMAINS);
    return cached ? JSON.parse(cached) : [];
  });


  // Domínios selecionados são da conta anterior: trocar de conta zera o filtro.
  useAoTrocarConta(() => setSelectedDomains([]));

  // Cache localStorage
  useEffect(() => localStorage.setItem(CACHE_KEYS.START_DATE, startDate), [startDate]);
  useEffect(() => localStorage.setItem(CACHE_KEYS.END_DATE, endDate), [endDate]);

  useEffect(() => {
    localStorage.setItem(CACHE_KEYS.DOMAINS, JSON.stringify(selectedDomains));
  }, [selectedDomains]);


  const { data: domains = [] } = useQuery({
    queryKey: ['domains'],
    queryFn: () => getDomains(),
  });

  const {
    data: adUnitData,
    isLoading: isLoadingAdUnit,
    isError: isErrorAdUnit,
    error: errorAdUnit,
  } = useQuery({
    queryKey: ['ad_unit', selectedDomains, startDate, endDate],
    queryFn: () =>
      getAdUnitData({
        domains: selectedDomains,
        start: startDate,
        end: endDate,
      }),
    enabled: true,
    retry: 2,
    retryDelay: 1000,
  });


  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs md:text-sm text-gray-600">Análise de performance e métricas</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3 bg-white rounded-lg p-2 md:p-3 shadow-sm border border-gray-200">

        <NetworkSelector variant="filtro" />
        <div className="hidden lg:block h-6 w-px bg-gray-300" />
        <CompactDomainSelect value={selectedDomains} onChange={setSelectedDomains} domains={domains} />
          <div className="hidden sm:block h-6 w-px bg-gray-300" />
          <CompactDateRangePicker startDate={startDate} endDate={endDate} onDateChange={handleDateChange} />

      </div>

        <AdUnitView data={adUnitData} isLoading={isLoadingAdUnit} isError={isErrorAdUnit} error={errorAdUnit} />
    </div>
  );
}
